// =====================================================================
// src/screens/v2/OnboardingV2.jsx
//
// V2 Welcome / Onboarding screen.
//
// Shown after sign-in for a user who hasn't joined a batch yet. Offers
// two clear paths:
//   1. Find or create a batch (V1 flow)
//   2. Browse local businesses (V2 new — skip batch requirement)
//
// This is V2's key insight: businesses don't require batch membership.
// Users can engage with local businesses immediately, even before
// finding their batchmates.
//
// Props:
//   onBack          — return to V1 / sign-out
//   onOpenBatch     — opens batch-search flow (placeholder in prototype)
//   onOpenDirectory — opens Business Directory
// =====================================================================

import React, { useState, useEffect } from 'react';
import { Users, ShoppingBag, ArrowLeft } from 'lucide-react';
import './OnboardingV2.css';

export default function OnboardingV2({ onBack, onOpenBatch, onOpenDirectory }) {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  function handleFindBatch() {
    if (onOpenBatch) onOpenBatch();
    else setToast('Find/create batch flow — handled in V1');
  }

  function handleBrowseBusinesses() {
    if (onOpenDirectory) onOpenDirectory();
    else setToast('Opens Business Directory');
  }

  return (
    <div className="v2-onboarding">
      {/* Logo header */}
      <div className="v2on-logo-header">
        <div className="v2on-logo-brand">
          <div className="v2on-logo-mark">
            {/* Aangan brand logo from /public/aangan-192.png — same asset
                used as the app icon (manifest), so the header reads as
                authentically branded. Replaces the inline SVG that was
                rendering hard-to-read at small sizes against the orange
                tile. onError hides the img cleanly if the file ever
                goes missing, rather than showing a broken-image icon. */}
            <img src="/aangan-192.png" alt="Aangan"
                 style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                 onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div className="v2on-logo-text">
            <div className="v2on-logo-name">Aangan</div>
          </div>
        </div>
        <div className="v2on-from">
          <span>FROM</span>
          {/* Real AapSaathi logo file — was a 🪔 emoji glyph, which
              renders as an orange-blob diya inconsistent with the rest
              of the app's branding. Same image used in V1's Loading
              screen + AuthOptions footer. onError hides if missing
              instead of showing a broken-image icon. */}
          <img src="/aap-saathi-logo.png" alt="AapSaathi"
               style={{ height: 16, width: 'auto' }}
               onError={(e) => { e.target.style.display = 'none'; }} />
          <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontStyle: 'italic' }}>
            AapSaathi
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="v2on-body">
        <div className="v2on-icon">
          <Users size={32} strokeWidth={2} />
        </div>

        <h1 className="v2on-title">Welcome to Aangan</h1>
        <p className="v2on-sub">
          Connect with your batchmates and discover local businesses from
          people you trust.
        </p>

        <div className="v2on-buttons">
          <button className="v2on-btn v2on-btn-primary" onClick={handleFindBatch}>
            <Users size={16} />
            Find or create a batch
          </button>

          <div className="v2on-divider">
            <span>or</span>
          </div>

          <button className="v2on-btn v2on-btn-ghost" onClick={handleBrowseBusinesses}>
            <ShoppingBag size={16} />
            Browse local businesses
          </button>
        </div>

        <div className="v2on-explainer">
          <div className="v2on-explainer-row">
            <span className="v2on-explainer-dot" style={{ background: 'var(--accent)' }} />
            <span>Find your school, college, or office mates</span>
          </div>
          <div className="v2on-explainer-row">
            <span className="v2on-explainer-dot" style={{ background: 'var(--gold)' }} />
            <span>Discover trusted local businesses around you</span>
          </div>
          <div className="v2on-explainer-row">
            <span className="v2on-explainer-dot" style={{ background: 'var(--success)' }} />
            <span>Aangan never collects your location</span>
          </div>
        </div>

        <div className="v2on-footer">
          <button className="v2on-text-link" onClick={onBack}>
            Sign out
          </button>
        </div>
      </div>

      {toast && <div className="v2on-toast">{toast}</div>}
    </div>
  );
}
